import React, { useEffect, useRef, useState } from "react";
import { SuperAdminShell } from "../components/SuperAdminShell";
import { useSuperAdminTenant } from "../context/SuperAdminTenantContext";
import saApi from "../lib/saApi";
import { toast } from "sonner";
import { KeySquare, Plus, Copy, Ban, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const PAGE_SIZE = 20;

export default function SASsoKeys() {
  const { tenantId, tenants } = useSuperAdminTenant();
  const [keys, setKeys] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);

  const load = (p) => {
    const params = { active_only: !showRevoked, limit: PAGE_SIZE, offset: p * PAGE_SIZE };
    if (tenantId && tenantId !== "all") params.tenant_id = tenantId;
    saApi.get("/sso-keys", { params })
      .then(({ data }) => {
        if (data.total > 0 && p * PAGE_SIZE >= data.total) {
          const lastPage = Math.max(0, Math.ceil(data.total / PAGE_SIZE) - 1);
          setPage(lastPage);
          return load(lastPage);
        }
        setKeys(data.items); setTotal(data.total);
      })
      .catch(() => { setKeys([]); setTotal(0); });
  };

  const filterKey = `${tenantId}|${showRevoked}`;
  const prevFilterKey = useRef(filterKey);
  useEffect(() => {
    if (filterKey !== prevFilterKey.current) {
      prevFilterKey.current = filterKey;
      if (page !== 0) { setPage(0); return; } // effect re-runs with page=0, fetches once below
    }
    load(page);
    /* eslint-disable-line react-hooks/exhaustive-deps */
  }, [filterKey, page]);

  const revoke = async (k) => {
    if (!window.confirm(`Revoke key "${k.name}" for ${k.tenant_name}?`)) return;
    try { await saApi.delete(`/sso-keys/${k.id}`); toast.success("Key revoked"); load(page); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <SuperAdminShell
      title="SSO Keys"
      actions={
        <Button data-testid="sa-sso-create-open" onClick={() => setOpen(true)}
                className="text-white" style={{ background: "linear-gradient(135deg,#22D3EE,#A78BFA)" }}>
          <Plus className="w-4 h-4 mr-1.5" />New key
        </Button>
      }
    >
      <div className="px-5 md:px-8 py-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-[11px] text-white/40 max-w-2xl">
            Cross-product SSO keys let external products exchange short-lived tokens for EIP sessions
            (<code>POST /api/sso/exchange</code>). Create or revoke on behalf of any tenant in scope — the same
            keys also appear self-service in each tenant&apos;s Admin Console → SSO Keys.
          </p>
          <button data-testid="sa-sso-toggle-revoked" onClick={() => setShowRevoked((v) => !v)}
                  className="text-[11px] text-cyan-300 hover:text-white whitespace-nowrap">
            {showRevoked ? "Hide revoked" : "Show revoked"}
          </button>
        </div>
        <div className="rounded-2xl border border-white/10 overflow-hidden backdrop-blur-md" style={{ background: "rgba(255,255,255,0.02)" }}>
          <table className="w-full text-sm" data-testid="sa-sso-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10 bg-white/5">
                <th className="px-4 py-3">Tenant</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Key</th>
                <th className="px-4 py-3">Audience</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {keys === null ? (
                <tr><td colSpan={7} className="px-4 py-6 text-white/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
              ) : keys.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-white/40">No {showRevoked ? "" : "active "}SSO keys</td></tr>
              ) : keys.map((k) => (
                <tr key={k.id} data-testid="sa-sso-row" className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-4 py-2 text-white">{k.tenant_name}</td>
                  <td className="px-4 py-2 text-white/90">{k.name}</td>
                  <td className="px-4 py-2 font-mono text-white/50 text-xs">{k.api_key_hint}</td>
                  <td className="px-4 py-2 text-white/60 text-xs">{k.audience}</td>
                  <td className="px-4 py-2 text-white/50 text-xs whitespace-nowrap">{new Date(k.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2">
                    {k.revoked_at ? <Badge className="bg-red-500/15 text-red-300 border-0 text-[10px]">Revoked</Badge>
                                  : <Badge className="bg-emerald-500/15 text-emerald-300 border-0 text-[10px]">Active</Badge>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {!k.revoked_at && (
                      <Button data-testid={`sa-sso-revoke-${k.id}`} size="sm" variant="ghost" onClick={() => revoke(k)}
                              className="h-7 text-xs text-red-300 hover:text-red-200 hover:bg-red-500/10">
                        <Ban className="w-3.5 h-3.5 mr-1" />Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-xs text-white/50">
            <span data-testid="sa-sso-page-info">Page {page + 1} of {pageCount} · {total} key{total === 1 ? "" : "s"}</span>
            <div className="flex items-center gap-2">
              <Button data-testid="sa-sso-prev-page" size="sm" variant="ghost" disabled={page === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))} className="h-7 text-xs text-white/70 hover:text-white">
                <ChevronLeft className="w-3.5 h-3.5 mr-1" />Prev
              </Button>
              <Button data-testid="sa-sso-next-page" size="sm" variant="ghost" disabled={page >= pageCount - 1}
                      onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} className="h-7 text-xs text-white/70 hover:text-white">
                Next<ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {open && <CreateKeyDialog tenants={tenants} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); setPage(0); load(0); }} copy={copy} />}
    </SuperAdminShell>
  );
}

function CreateKeyDialog({ tenants, onClose, onCreated, copy }) {
  const [tenantId, setTenantId] = useState(tenants[0]?.id || "");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async () => {
    if (!tenantId) { toast.error("Choose a tenant"); return; }
    if (!name.trim()) { toast.error("Name the key"); return; }
    setSaving(true);
    try {
      const { data } = await saApi.post("/sso-keys", { tenant_id: tenantId, name: name.trim() });
      setCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create key"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><KeySquare className="w-4 h-4" />New SSO key</DialogTitle></DialogHeader>
        {!created ? (
          <div className="space-y-3 py-1">
            <div>
              <Label>Tenant</Label>
              <Select value={tenantId} onValueChange={setTenantId}>
                <SelectTrigger data-testid="sa-sso-form-tenant" className="mt-1.5"><SelectValue placeholder="Choose tenant" /></SelectTrigger>
                <SelectContent>{tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Key name</Label>
              <Input data-testid="sa-sso-form-name" className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)} placeholder="Billing Portal" />
            </div>
          </div>
        ) : (
          <div className="space-y-2 py-1">
            <div className="text-xs text-muted-foreground">Copy these now — shown only once.</div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">API key</Label>
              <div className="flex items-center gap-1.5">
                <code className="text-[11px] font-mono break-all flex-1">{created.api_key}</code>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copy(created.api_key)}><Copy className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Shared secret (HS256)</Label>
              <div className="flex items-center gap-1.5">
                <code className="text-[11px] font-mono break-all flex-1">{created.shared_secret}</code>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copy(created.shared_secret)}><Copy className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          {!created ? (
            <>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button data-testid="sa-sso-form-submit" onClick={submit} disabled={saving}
                      className="text-white" style={{ background: "linear-gradient(135deg,#22D3EE,#A78BFA)" }}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create key"}
              </Button>
            </>
          ) : (
            <Button data-testid="sa-sso-form-done" onClick={onCreated} className="text-white" style={{ background: "linear-gradient(135deg,#22D3EE,#A78BFA)" }}>
              Done, I saved them
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
